import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { addDays, getYear, startOfWeek, subYears } from 'date-fns';
import { PropsWithChildren } from 'react';
import Chart from 'react-apexcharts';
import NumberFormat from 'react-number-format';
import { useHistory, useParams } from 'react-router-dom';
import { useTitle } from 'shared/hooks';
import { useProduct } from 'shared/queries';
import { History } from 'shared/types';

function uniqueByProperty<T>(property: keyof T, mapper?: (value: any) => any) {
    return (list: T[]): T[] => [
        ...list
            .reduce((map, item) => {
                const key = mapper ? mapper(item[property]) : item[property];
                map.set(key, item);
                return map;
            }, new Map())
            .values(),
    ];
}

const byStartOfWeek = (date: string) => startOfWeek(addDays(new Date(date), 1)).getTime();

const toNumber = (value?: string | number, percent: boolean = false) => () => (
    <NumberFormat
        value={value}
        displayType='text'
        thousandSeparator={true}
        prefix={percent ? undefined : '$'}
        suffix={percent ? '%' : undefined}
    />
);

const toText = (text?: string) => () => text;

function TwoColumnGrid({ children }: PropsWithChildren<{}>) {
    return (
        <Box display='grid' gridTemplateColumns='1fr 1fr' gap='10px' padding={1.25}>
            {children}
        </Box>
    );
}

export function ProductDetailPage() {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const goBack = () => {
        history.goBack();
    };
    useTitle(`Product ${id}`);

    const getUniqueDate = uniqueByProperty<History>('updatedAt', byStartOfWeek);

    const { product, isLoading } = useProduct(id);

    const belnickData = getUniqueDate(product?.productHistory || []).map(history => ({
        x: byStartOfWeek(history.updatedAt),
        y: history.newPrice,
    }));

    const competitorData = (product?.competitors || []).map(competitor => ({
        name: competitor.description,
        data: getUniqueDate(competitor.competitorHistory).map(x => ({
            x: byStartOfWeek(x.updatedAt),
            y: x.newPrice,
        })),
    }));

    const chartOptions = {
        stroke: {
            curve: 'straight' as const,
            lineCap: 'round' as const,
        },
        xaxis: {
            type: 'datetime' as const,
        },
    };

    const chartData = [
        {
            name: 'belnick',
            data: belnickData,
        },
        ...competitorData,
    ];

    if (isLoading) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' height='200px'>
                <CircularProgress />
            </Box>
        );
    }

    const today = new Date();
    const currentYear = getYear(today);
    const lastYear = getYear(subYears(today, 1));

    const details: [string, () => string | undefined | JSX.Element][] = [
        ['Name:', toText(product?.productName)],
        ['SKU:', toText(product?.productCode)],
        ['Category:', toText(product?.category)],
        ['Kit Flag:', toText(product?.kitFlag)],
        ['Current Price:', toNumber(product?.currentPrice)],
        ['Product Tier:', toText(product?.productTier)],
        ['Product Margin:', toNumber((Number(product?.productMargin) * 100).toFixed(1), true)],
        ['FIFO Cost:', toNumber(product?.productCostUnit.toFixed(2))],
        [`${currentYear} Quantity Sold`, toText(product?.details?.ytdqty)],
        [`${currentYear} Sales`, toNumber(product?.details?.ytdSales)],
        [`${lastYear} Quantity Sold`, toText(product?.details?.lastYearQTY)],
        [`${lastYear} Sales`, toNumber(product?.details?.lastYearSales)],
    ];

    return (
        <div>
            <Button color='secondary' sx={{ margin: 1.25 }} variant='outlined' onClick={goBack}>
                go back
            </Button>
            <TwoColumnGrid>
                <div>
                    <Typography>Details</Typography>
                    <TwoColumnGrid>
                        {details.map(([label, detail], i) => (
                            <div key={i}>
                                <label>
                                    <strong>{label}</strong>
                                </label>
                                <p>{detail()}</p>
                            </div>
                        ))}
                    </TwoColumnGrid>
                </div>
                <div>
                    <Typography>Price History</Typography>
                    {product && <Chart options={chartOptions} series={chartData} type='line' />}
                </div>
            </TwoColumnGrid>
        </div>
    );
}
